/* eslint-disable no-unused-expressions */
import { print } from "graphql";

import { getLanguageCode } from "web/Layout/Translations/LanguageWrapper";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import deleteGraphqlExtraSpaces from "web/utils/system/query/deleteGraphqlExtraSpaces";
import graphQlAsGet from "web/utils/system/query/graphQlAsGet";

import { request } from "web/api";

import MapWrapper from "./mapWrapper";
import { getOperationName, getSelectionSetName } from "./utils";

const idsFacetsStatsQueries = ["productsIdsFacetsStats", "productsSearch"];
const productsShortQuery = ["productsInListByIds", "productsInWishlistByIds"];
const productsFullQuery = ["productsByIds"];
const menuQuery = ["menu"];
const customerMenuQuery = ["customerMenu"];
const cmsBlocksQuery = ["footer"];
const categoryCountsQuery = ["categoryCounts"];

const mapData = new MapWrapper();
const fetchPageSize = 100;

class DataAbstract {
  constructor({ query, ttl, endpoint, identifier }) {
    this.ttlPrivate = ttl;
    this.queryPrivate = query;
    this.endpointPrivate = endpoint;
    this.identifierPrivate = identifier;
  }

  get endpoint() {
    return this.endpointPrivate;
  }

  get identifier() {
    return this.identifierPrivate;
  }

  get query() {
    return this.queryPrivate;
  }

  get ttl() {
    return this.ttlPrivate;
  }

  async getItemsByKeys({
    ids,
    storeId,
    token,
    queryName,
    additionalParameters,
    additionalOptions,
  }) {
    const itemsCached = [];
    const idsLack = [];
    const idsWaiting = [];
    const itemsWaitingFlatted = [];
    let itemsLackFlatted = [];
    const isIdsFacetsStatsData = idsFacetsStatsQueries.includes(queryName);
    const isShortProducts = productsShortQuery.includes(queryName);
    const isFullProducts = productsFullQuery.includes(queryName);
    const isMenu = menuQuery.includes(queryName);
    const isCustomerMenu = customerMenuQuery.includes(queryName);
    const isCmsBlock = cmsBlocksQuery.includes(queryName);
    const isBlock = isMenu || isCustomerMenu || isCmsBlock;
    const isCategoryCounts = categoryCountsQuery.includes(queryName);

    if (isArrayHasItems(ids)) {
      await Promise.all(
        ids.map(async (id) => {
          const dataFromCache = await this.getItem(id);
          switch (true) {
            case !!additionalParameters: {
              !idsLack.includes(id) && idsLack.push(id);
              break;
            }
            case dataFromCache?.isShortProduct && isFullProducts: {
              !idsLack.includes(id) && idsLack.push(id);
              break;
            }
            case !!dataFromCache: {
              itemsCached.push(dataFromCache);
              break;
            }
            case !!mapData.has(id): {
              idsWaiting.push(id);
              break;
            }
            default: {
              !idsLack.includes(id) && idsLack.push(id);
            }
          }
          return null;
        })
      );
    }

    if (isArrayHasItems(idsLack)) {
      mapData.set(
        idsLack,
        this.getLackData({
          ids: idsLack,
          storeId,
          token,
          isIdsFacetsStatsData,
          additionalParameters,
          isBlock,
          isMenu,
          isCustomerMenu,
          isCmsBlock,
          additionalOptions,
        })
      );
    }

    if (isArrayHasItems(idsWaiting)) {
      await Promise.all(
        idsWaiting.map(async (idWaiting) => {
          const dataWaitingPages = await mapData.getPromiseByKey(idWaiting);
          const currentDataFlatted =
            dataWaitingPages
              ?.map((dataWaiting) => this.getFlattedData(dataWaiting))
              .flat() || [];
          let currentDataById = isArrayHasItems(currentDataFlatted)
            ? currentDataFlatted.find((currentItem) => {
                return `${currentItem[this.identifier]}` === `${idWaiting}`;
              })
            : null;
          if (isIdsFacetsStatsData) {
            currentDataById = currentDataFlatted?.[0];
          }
          if (currentDataById) {
            itemsWaitingFlatted.push(currentDataById);
          }
        })
      );
    }

    if (isArrayHasItems(idsLack)) {
      const dataLackPages = await mapData.get(idsLack);
      mapData.delete(idsLack);
      itemsLackFlatted =
        dataLackPages
          ?.map((dataLack) => this.getFlattedData(dataLack))
          .flat() || [];
      const itemIdentifier =
        isIdsFacetsStatsData || isBlock ? ids[0] : this.identifier;
      await this.setItemsByKey(
        itemIdentifier,
        itemsLackFlatted,
        ids,
        isIdsFacetsStatsData,
        isShortProducts,
        isBlock,
        isCategoryCounts,
        itemsCached
      );
    }

    const collectiveData = [
      ...itemsCached,
      ...itemsWaitingFlatted,
      ...itemsLackFlatted,
    ];

    if (isIdsFacetsStatsData || isBlock) {
      return collectiveData[0];
    }

    if (isArrayHasItems(ids)) {
      return ids.reduce((resultIds, currentId) => {
        const currentDataById = collectiveData.find(
          (collectiveDataItem) =>
            collectiveDataItem &&
            `${collectiveDataItem[this.identifier]}` === `${currentId}`
        );
        return currentDataById ? [...resultIds, currentDataById] : resultIds;
      }, []);
    }
    return [];
  }

  getLackData({
    ids,
    storeId,
    token,
    isIdsFacetsStatsData,
    additionalParameters,
    isBlock,
    isMenu,
    isCustomerMenu,
    isCmsBlock,
    additionalOptions,
  }) {
    const pages = Math.ceil(ids.length / fetchPageSize);
    const actions = [];
    const queryProcessed = print(this.query);
    const queryProcessedWithoutSpaces =
      deleteGraphqlExtraSpaces(queryProcessed);
    const operationName = getOperationName(this.query);

    if (isIdsFacetsStatsData) {
      const options = {
        method: "POST",
        body: JSON.stringify({
          query: queryProcessedWithoutSpaces,
          operationName,
          variables: JSON.parse(ids[0])?.variables,
        }),
      };
      actions.push(graphQlAsGet(this.endpoint, options, request));
      return Promise.all(actions);
    }

    if (isBlock) {
      const options = {
        method: "POST",
        body: JSON.stringify({
          query: queryProcessedWithoutSpaces,
          operationName,
          variables: {
            ...(isCmsBlock ? { id: ids[0], lang: getLanguageCode() } : {}),
            ...(isCustomerMenu ? { storeId, token } : {}),
            ...(isMenu
              ? {
                  storeId,
                  token,
                  lang: getLanguageCode(),
                }
              : {}),
          },
        }),
      };

      actions.push(graphQlAsGet(this.endpoint, options, request));
      return Promise.all(actions);
    }

    for (let page = 1; page <= pages; page += 1) {
      const pageIds = ids.slice(
        (page - 1) * fetchPageSize,
        page * fetchPageSize
      );

      const options = {
        method: "POST",
        body: JSON.stringify({
          query: queryProcessedWithoutSpaces,
          operationName,
          variables: {
            storeId,
            token,
            ids: pageIds,
            lang: getLanguageCode(),
            ...(additionalParameters ? { additionalParameters } : {}),
            ...(additionalOptions ?? {}),
          },
        }),
      };
      actions.push(graphQlAsGet(this.endpoint, options, request));
    }
    return Promise.all(actions);
  }

  getFlattedData(data) {
    const operationName = getOperationName(this.query);
    const selectionSetName = getSelectionSetName(this.query);

    if (
      data &&
      data.data &&
      selectionSetName &&
      selectionSetName.length &&
      data.data[selectionSetName[0]]
    ) {
      switch (operationName) {
        case "campaignGraphics":
        case "cmsBlocks":
          return data.data[selectionSetName[0]] || [];
        default:
          return data.data[selectionSetName[0]].edges &&
            data.data[selectionSetName[0]].edges.length
            ? data.data[selectionSetName[0]].edges.reduce((result, current) => {
                const name = "node";
                return current &&
                  Object.prototype.hasOwnProperty.call(current, name)
                  ? [...result, current[name]]
                  : result;
              }, [])
            : [];
      }
    }
    return [];
  }

  async setItemsByKey(
    key,
    value,
    ids,
    isIdsFacetsStatsData,
    isShortProducts,
    isBlock,
    isCategoryCounts,
    itemsCached
  ) {
    const setItemToStorage = async (item) => {
      if (Object.prototype.hasOwnProperty.call(item, key)) {
        const additionalItemParams = {
          ...(isShortProducts ? { isShortProduct: true } : {}),
        };
        await this.setItem(item[key], { ...item, ...additionalItemParams });
      }
      if (isIdsFacetsStatsData) {
        await this.setItem(key, item);
      }
      if (isBlock) {
        await this.setItem(key, { ...item, block_id: key });
      }
    };

    /* eslint-disable */
    if (
      key &&
      isArrayHasItems(ids) &&
      value?.length < ids?.length &&
      isCategoryCounts
    ) {
      await Promise.all(
        ids.map((id) => {
          const itemToSet = value.find((item) => item[key] == id) ||
            itemsCached.find((item) => item[key] == id) || {
              [key]: `${id}`,
            };
          setItemToStorage(itemToSet);
          return null;
        })
      );
      return;
    }

    if (key && isArrayHasItems(value)) {
      await Promise.all(
        value.map((item) => {
          setItemToStorage(item);
          return null;
        })
      );
    }
    return [];
    /* eslint-enable */
  }
}

export default DataAbstract;
